import { io } from 'socket.io-client';
import { newAuth } from '@/firebase/firebaseInit';
import { onIdTokenChanged } from 'firebase/auth';
import { WEBSOCKET_URL } from '@/constants/constants';

// WebSocket event names — must match the backend WebsocketEventType enum
const WS_EVENTS = {
  // Client to Server
  SUBSCRIBE_TO_ALERTS: 'subscribe-to-alerts',
  PING: 'ping',

  // Server to Client
  NEW_ALERT: 'new-alert', // backend signals that a new alert was created
  PONG: 'pong',
  ERROR: 'error',
};

/**
 * WebSocketService — lightweight notification channel for real-time alert updates.
 *
 * Responsibilities:
 *   - Maintain an authenticated Socket.IO connection to the WebSocket server
 *   - Subscribe to alerts so the server knows to route notifications here
 *   - On 'new-alert': trigger an HTTP call to refresh the unread count in the store
 *
 * All alert data (list, count) is fetched via HTTP. WebSocket only signals
 * that something changed, keeping the WS layer simple and stateless.
 */
class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.isSubscribed = false;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.store = null;
    this.tokenRefreshUnsubscribe = null;
  }

  getWebSocketUrl() {
    return WEBSOCKET_URL || 'http://localhost:8080';
  }

  /**
   * Initialise the WebSocket connection.
   * Called once on app mount after the user is authenticated.
   * Guard prevents duplicate sockets if the component remounts.
   */
  async initialize(store) {
    // Already connected or connecting — just update the store reference
    if (this.socket) {
      this.store = store;
      return;
    }

    this.store = store;
    await this.initializeSocket();

    // Cancel any previous token-refresh listener before registering a new one
    if (this.tokenRefreshUnsubscribe) {
      this.tokenRefreshUnsubscribe();
    }

    // Re-connect with a fresh token when Firebase rotates it
    this.tokenRefreshUnsubscribe = onIdTokenChanged(newAuth, async (user) => {
      if (user && this.socket && this.isConnected) {
        try {
          const newToken = await user.getIdToken(false);
          this.socket.auth.token = newToken;
          // Reconnect to pick up the new token
          this.socket.disconnect();
          this.socket.connect();
        } catch (error) {
          console.error('Failed to refresh WebSocket token:', error);
        }
      }
    });
  }

  async initializeSocket() {
    const wsUrl = this.getWebSocketUrl();

    try {
      const { firebaseAuthService } = await import('./firebaseAuthService');
      const token = await firebaseAuthService.getIdToken();

      this.socket = io(wsUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        timeout: 10000,
      });
    } catch (error) {
      console.error('Failed to get auth token for WebSocket — connecting without token:', error);
      this.socket = io(wsUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        timeout: 10000,
      });
    }

    this.setupEventHandlers();
  }

  setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      this.isConnected = true;

      if (this.store) {
        this.store.dispatch('alert/setConnectionStatus', true);
      }

      // Subscribe to alerts so the server routes new-alert events to us
      this.subscribeToAlerts();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from WebSocket server:', reason);
      this.isConnected = false;
      this.isSubscribed = false;

      if (this.store) {
        this.store.dispatch('alert/setConnectionStatus', false);
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message || error.description || error);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Reconnected to WebSocket server, attempt:', attemptNumber);
      this.isConnected = true;

      if (this.store) {
        this.store.dispatch('alert/setConnectionStatus', true);
      }

      // Re-subscribe after reconnection
      this.subscribeToAlerts();
    });

    this.socket.on('reconnect_failed', () => {
      console.error('Failed to reconnect to WebSocket server after max attempts');
    });

    // Server signals that a new alert was created — fetch the updated unread count via HTTP
    this.socket.on(WS_EVENTS.NEW_ALERT, () => {
      console.log('New alert notification received — refreshing unread count');
      if (this.store) {
        this.store.dispatch('alert/fetchUnreadCount');
      }
    });

    // Error from server
    this.socket.on(WS_EVENTS.ERROR, (data) => {
      console.error('WebSocket error from server:', data);
    });

    // Health check response
    this.socket.on(WS_EVENTS.PONG, () => {
      console.log('Pong received');
    });
  }

  /**
   * Send the subscribe-to-alerts handshake to the server.
   * The server uses the authenticated token to know which alerts belong to this user.
   */
  subscribeToAlerts() {
    if (this.socket && this.isConnected) {
      this.socket.emit(WS_EVENTS.SUBSCRIBE_TO_ALERTS);
      this.isSubscribed = true;
      console.log('Subscribed to alert notifications');
    } else {
      console.warn('Cannot subscribe — socket not connected');
    }
  }

  ping() {
    if (this.socket && this.isConnected) {
      this.socket.emit(WS_EVENTS.PING);
    }
  }

  connect() {
    if (this.socket && !this.isConnected) {
      this.socket.connect();
    }
  }

  disconnect() {
    if (this.tokenRefreshUnsubscribe) {
      this.tokenRefreshUnsubscribe();
      this.tokenRefreshUnsubscribe = null;
    }

    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;
    this.isSubscribed = false;
  }

  getConnectionStatus() {
    return this.isConnected;
  }

  isAlertsSubscribed() {
    return this.isSubscribed;
  }
}

export const websocketService = new WebSocketService();
export default websocketService;
